import {
    differenceInDays,
    endOfWeek,
    format,
    lastDayOfMonth,
    parseISO,
    startOfWeek,
    subMonths,
    subWeeks,
} from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import { ru } from 'date-fns/locale';

type YYYY = `${number}${number}${number}${number}`
type MM = `${number}${number}`
type DD = `${number}${number}`
type hh = `${number}${number}`
type mm = `${number}${number}`
type ss = `${number}${number}`

export type DateString = `${YYYY}-${MM}-${DD}`;
export type TimeString = `${hh}:${mm}:${ss}`;
export type DateTimeString = `${DateString} ${TimeString}`;

export const getFormatDatesForDatepicker = (initialDates: string[] | string, pickerType: 'date' | 'month'): string | void => {
    if (!initialDates) { return; }
    const pattern = pickerType === 'month' ? 'LLLL yyyy' : 'd MMMM yyyy';
    if (typeof initialDates === 'string') {
        const formattingDate = initialDates?.length === 7 ? `${initialDates}-01` : initialDates;
        return format(parseISO(formattingDate), pattern, { locale: ru });
    }
    if (initialDates instanceof Array) {
        if (initialDates.length === 1) {
            initialDates[0] = initialDates[0]?.length === 7 ? `${initialDates[0]}-01` : initialDates[0];
            return format(parseISO(initialDates[0]), pattern, { locale: ru });
        }
        if (initialDates.length === 2) {
            initialDates[0] = initialDates[0]?.length === 7 ? `${initialDates[0]}-01` : initialDates[0];
            initialDates[1] = initialDates[1]?.length === 7 ? `${initialDates[1]}-01` : initialDates[1];
            const isNeedToSwap = initialDates[0].valueOf() > initialDates[1].valueOf();
            const yearOfFirstDate = parseISO(initialDates[`${isNeedToSwap ? 1 : 0}`]).getFullYear();
            const yearOfSecondDate = parseISO(initialDates[`${isNeedToSwap ? 0 : 1}`]).getFullYear();
            const formatSecondDate = format(
                parseISO(initialDates[`${isNeedToSwap ? 0 : 1}`]),
                pattern,
                { locale: ru },
            );
            let formatFirstDate;
            if (yearOfFirstDate === yearOfSecondDate) {
                const currentPattern = pickerType === 'month' ? 'LLLL' : 'd MMMM';
                formatFirstDate = format(
                    parseISO(initialDates[`${isNeedToSwap ? 1 : 0}`]),
                    currentPattern,
                    { locale: ru },
                );
            } else {
                formatFirstDate = format(parseISO(initialDates[`${isNeedToSwap ? 1 : 0}`]), pattern, { locale: ru });
            }
            return initialDates[0] === initialDates[1] ? `${formatSecondDate}` : `${formatFirstDate} - ${formatSecondDate}`;
        }
    }
};

// TODO вставить эту функцию во всех местах, где получаем текущую дату,
// например, вместо new Date().toISOString().substr(0, 7)

// Получить текущую дату, в приложении используем таймзону `Europe/Moscow`
export const getCurrentDate = (formatType: string = 'dd-MM-yyyy'): string => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    const zonedDate = utcToZonedTime(utcDate, 'Europe/Moscow');
    return format(zonedDate, formatType);
};

export const getPreviousDate = (formatType: string = 'dd-MM-yyyy', days: number = 1): string => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    const zonedDate = utcToZonedTime(utcDate.setDate(utcDate.getDate() - days), 'Europe/Moscow');

    return format(zonedDate, formatType);
};

// Сколько дней прошло с понедельника. В getDay() воскресенье - 0, а неделя начинается
// с понедельника: "+ 1 - getDay()" в воскресенье давал понедельник следующей недели.
const getDaysSinceMonday = (date: Date): number => (date.getDay() + 6) % 7;

export const getFullPreviousWeek = (formatType: string = 'dd-MM-yyyy'): string[] => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    const zonedDateStart = utcToZonedTime(utcDate.setDate(utcDate.getDate() - getDaysSinceMonday(utcDate)), 'Europe/Moscow');
    const start = format(subWeeks(zonedDateStart, 1), formatType);
    // utcDate уже понедельник текущей недели, день до него - воскресенье прошлой
    const zonedDateEnd = utcToZonedTime(utcDate.setDate(utcDate.getDate() - 1), 'Europe/Moscow');
    const end = format(zonedDateEnd, formatType);
    return [start, end];
};

export const getCurrentWeek = (formatType: string = 'dd-MM-yyyy'): string[] => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    const zonedDateStart = utcToZonedTime(utcDate.setDate(utcDate.getDate() - 6), 'Europe/Moscow');
    const start = format(zonedDateStart, formatType);
    const end = getCurrentDate(formatType);
    return [start, end];
};

export const getFullCurrentWeek = (formatType: string = 'dd-MM-yyyy'): string[] => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    const zonedDateStart = utcToZonedTime(utcDate.setDate(utcDate.getDate() - getDaysSinceMonday(utcDate)), 'Europe/Moscow');
    const start = format(zonedDateStart, formatType);
    const end = getCurrentDate(formatType);
    return [start, end];
};

export const getFullPreviousMonth = (formatType: string = 'dd-MM-yyyy', startDate: Date): string[] => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    utcDate.setDate(1);
    const zonedDateStart = utcToZonedTime(utcDate, 'Europe/Moscow');
    const prevZonedDateStart = subMonths(zonedDateStart, 1);
    const start = format(prevZonedDateStart < startDate ? startDate : prevZonedDateStart, formatType);
    const zonedDateEnd = utcToZonedTime(utcDate.setDate(utcDate.getDate() - 1), 'Europe/Moscow');
    const end = format(startDate > zonedDateEnd ? startDate : zonedDateEnd , formatType);
    return [start, end];
};

export const getFullCurrentMonth = (formatType: string = 'dd-MM-yyyy'): string[] => {
    const start = getCurrentMonth(formatType);
    const end = getCurrentDate(formatType);
    return [start, end];
};

export const getPreviousMonth = (formatType: string = 'dd-MM-yyyy', month: number): string => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    utcDate.setDate(1);
    const zonedDate = utcToZonedTime(utcDate, 'Europe/Moscow');
    return format(subMonths(zonedDate, month), formatType);
};

export const getCurrentMonth = (formatType: string = 'dd-MM-yyyy'): string => {
    const utcDate = toDate(new Date(), { timeZone: 'UTC' });
    utcDate.setDate(1);
    const zonedDate = utcToZonedTime(utcDate, 'Europe/Moscow');
    return format(zonedDate, formatType);
};

export const getCurrentYear = (formatType: string = 'dd-MM-yyyy', startDate: Date = new Date()): string[] => {
    const utcDate = toDate(new Date(new Date().getFullYear(), 0, 1), { timeZone: 'UTC' });
    const zonedDateStart = utcToZonedTime(utcDate, 'Europe/Moscow');
    const currentStart = zonedDateStart > startDate ? zonedDateStart : startDate;
    const start = format(currentStart, formatType);
    const end = getCurrentDate(formatType);
    return [start, end];
};

// Получить отформатированную дату в читаемом виде, например:
// 1 апреля 2020 15:14
export const getFormatDate = (date: string | Date, formatType: string = 'd MMMM yyyy HH:mm'): string => {
    if (typeof date === 'string') {
        return format(new Date(parseISO(date)), formatType, { locale: ru });
    }
    return format(new Date(date), formatType, { locale: ru });
};

export const getLastDayOfCurrentMonth = (formatType: string = 'yyyy-MM-dd'): string => {
    const lastDay = lastDayOfMonth(new Date());
    const zonedDate = utcToZonedTime(lastDay, 'Europe/Moscow');
    return format(zonedDate, formatType);
};

//  Получить последний день месяца
export const getLastDayOfMonth = (value: string | Date): Date => {
    const date = new Date(value);
    const y = date.getFullYear();
    const m = date.getMonth();
    return new Date(y, m + 1, 0);
};

export const getFirstDayOfMonth = (value: string | Date): Date => {
    const date = new Date(value);
    const y = date.getFullYear();
    const m = date.getMonth();
    return new Date(y, m, 1);
};


export const formatDateInputPicker = (value: string, formatType="dd.MM.yy"): string =>{
    return value !== '' ? format(parseISO(value),formatType, { locale: ru }) : '';
};

export const getWeek = (value: string | Date, formatType: string = 'yyyy-MM-dd'): string[] | Date[] => {
    const date = value instanceof Date ? value : new Date(parseISO(value));
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek(date, { weekStartsOn: 1 });
    return [format(start, formatType), format(end, formatType)];
};

export const getDifferenceInDays = (dateOne: Date, dateTwo: Date): number => {
    return Math.abs(differenceInDays(dateOne, dateTwo));
};

export const getRemainingTime = (secondsLeft: number): string => {
    const days = Math.floor(secondsLeft / 86400);
    const hours = Math.floor((secondsLeft % 86400) / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;

    return [
        `${days} ${pluralRu(days, ['день', 'дня', 'дней'])}`,
        `${hours} ${pluralRu(hours, ['час', 'часа', 'часов'])}`,
        `${minutes} ${pluralRu(minutes, ['минута', 'минуты', 'минут'])}`,
        `${seconds} ${pluralRu(seconds, ['секунда', 'секунды', 'секунд'])}`,
    ].join(' ');
};

/**
 * Компактная длительность для метрик: 34462.4 → "9 ч 34 мин 22 сек". Нулевые части не выводятся.
 * Число и единица связаны неразрывным пробелом, чтобы перенос строки не отрывал их друг от друга.
 * Пустое значение — прочерк, чтобы не путать с нулём.
 */
export const formatDurationShort = (seconds: number | string | null | undefined): string => {
    if (seconds === null || seconds === undefined || seconds === '' || !Number.isFinite(Number(seconds))) {
        return '—';
    }
    const total = Math.round(Number(seconds));
    const units: [number, string][] = [[86400, 'д'], [3600, 'ч'], [60, 'мин'], [1, 'сек']];
    const parts = units
        .map(([size, label], i) => [Math.floor((i ? total % units[i - 1][0] : total) / size), label] as const)
        .filter(([n]) => n > 0)
        .map(([n, label]) => `${n}\u00A0${label}`);
    return parts.length ? parts.join(' ') : '0\u00A0сек';
};

/**
 * Склонение для русских существительных (1 день, 2 дня, 5 дней).
 * forms: [ед. ч., 2-4, 0/5-20/25-30...]
 */
export function pluralRu(n: number, forms: [string, string, string]): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
    return forms[2];
}
/**
 * Переводит общее количество минут в строку вида "N дней N часов N минут".
 * Пример: 195 → "3 часа 15 минут", 1440 → "1 день 0 часов 0 минут".
 */
export const formatMinutesToDuration = (totalMinutes: number): string => {
    if (totalMinutes == null || !Number.isFinite(totalMinutes)) {
        return '';
    }
    const isNegative = totalMinutes < 0;
    const total = Math.floor(Math.abs(totalMinutes));
    const days = Math.floor(total / (60 * 24));
    const hours = Math.floor((total % (60 * 24)) / 60);
    const minutes = total % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${pluralRu(days, ['день', 'дня', 'дней'])}`);
    if (hours > 0) parts.push(`${hours} ${pluralRu(hours, ['час', 'часа', 'часов'])}`);
    if (minutes > 0) parts.push(`${minutes} ${pluralRu(minutes, ['минуту', 'минуты', 'минут'])}`);
    const result = parts.length ? parts.join(' ') : '0 минут';
    return isNegative ? `- ${result}` : result;
};

export const getFormatTime = (
    date: Date = new Date(),
    format: 'HH:mm:ss' | 'HH:mm' | 'HH' | 'mm' | 'ss' = 'HH:mm:ss',
): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const padZero = (num: number): string => {
        return num < 10 ? `0${num}` : num.toString();
    };

    const formattedHours = padZero(hours);
    const formattedMinutes = padZero(minutes);
    const formattedSeconds = padZero(seconds);

    switch (format) {
    case 'HH:mm:ss':
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    case 'HH:mm':
        return `${formattedHours}:${formattedMinutes}`;
    case 'HH':
        return formattedHours;
    case 'mm':
        return formattedMinutes;
    case 'ss':
        return formattedSeconds;
    default:
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }
};
